import { Typography } from 'antd'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'
import { BaseViewFieldTemplate } from '@/Ship/Templates/Form/BaseViewFieldTemplate'

import type { FC, ReactNode } from 'react'
import type { TaskFormContextLike } from '../Contracts/Crud.types'

/** Ship-шаблон — .jsx без типів; children у пропсах відновлюємо аліасом. */
const ViewFieldTemplate = BaseViewFieldTemplate as FC<{ field: unknown; children?: ReactNode }>

/**
 * View-хелпери полів картки задачі (SPEC-008 §4.1, хвіст 05.2).
 *
 * ВАЖЛИВО про подвійний контракт view (урок 06 + фікс UI-огляду):
 *   1) ядро викликає `field.definition.view` ПОЗИЦІЙНО — `view(toJS(value), field.formContext)`
 *      (`Core/UI/Form/Stores/BaseFieldStore.ts:481`) — тут рахуємо ТЕКСТ значення;
 *   2) view-гілка `FormField` ре-викликає той самий view З ОБ'ЄКТОМ —
 *      `view({field, formContext, value})` (`FormField.tsx:380`), де `value` — вже
 *      порахований текст з кроку 1, — і рендерить РЕЗУЛЬТАТ БЕЗ шаблона з лейблом.
 *      Тому на об'єктному виклику самі загортаємо значення в Ship
 *      `BaseViewFieldTemplate` (лейбл у єдиному стилі), а порожнє («—») ховаємо
 *      цілком (null) — інакше на картці висять сироти-риски без підпису.
 */

/**
 * ISO-datetime → «22.09.2026, 13:57»; порожнє → «—».
 *
 * Без секунд: `toLocaleString` давав «02.09.2026, 00:00:00», а секунди в
 * картці завдання нічого не кажуть.
 */
export const formatDateTime = (value?: string | null): string => formatDateTimeUk(value)

/**
 * ISO-дата → «дд.мм.рррр» (uk-UA); порожнє → «—».
 *
 * Колонка «Дедлайн» у списку задач. Ядровий показ `date` форматує за
 * `AppConfig.dateDefaultFormat`, а це глобальні для застосунку «YYYY-MM-DD» —
 * тож укр. формат задаємо тут (поле виключене з ядрової трансформації через
 * `table.dataSourceNoTransform`, див. `TaskResource`).
 */
export const formatDate = (value?: string | null): string =>
    value ? new Date(value).toLocaleDateString('uk-UA') : '—'

/** Витрачений час у хвилинах → «Nг Mхв»; 0/порожнє/від'ємне → «—». */
export const formatMinutes = (total?: number | null): string => {
    if (typeof total !== 'number' || total <= 0) return '—'
    const hours = Math.floor(total / 60)
    const minutes = total % 60
    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}г`)
    if (minutes > 0) parts.push(`${minutes}хв`)
    return parts.length ? parts.join(' ') : '—'
}

/** Форма об'єктного ре-виклику FormField ({field, formContext, value}). */
interface ViewObjectArg {
    field?: unknown
    formContext?: TaskFormContextLike
    value?: unknown
}

const isViewObjectArg = (arg: unknown): arg is ViewObjectArg =>
    arg !== null && typeof arg === 'object' && !Array.isArray(arg) && 'value' in (arg as object)

/** Об'єктний виклик: порожнє значення ховаємо, заповнене — з лейблом у Ship-шаблоні. */
const wrapViewValue = (field: unknown, text: string): ReactNode => {
    if (text === '—' || text === '') return null
    if (!field) return text
    return <ViewFieldTemplate field={field}>{text}</ViewFieldTemplate>
}

/**
 * `completedAt` показуємо лише коли задача завершена (SPEC-008 §4.1); інакше поле
 * ховається цілком. Підтримує обидві конвенції виклику ядра (див. шапку файла).
 */
export const renderCompletedAt = (arg1: unknown, formContext?: TaskFormContextLike): ReactNode => {
    if (isViewObjectArg(arg1)) {
        const value = arg1.value
        const text = typeof value === 'string' ? value : formatDateTime(value as string | null | undefined)
        return wrapViewValue(arg1.field, text)
    }
    return formContext?.values?.status === 'completed' ? formatDateTime(arg1 as string | null | undefined) : '—'
}

/**
 * `timeSpentInMinutes` — «Nг Mхв»; без значення поле ховається цілком.
 * Підтримує обидві конвенції виклику ядра (див. шапку файла).
 */
export const renderTimeSpent = (arg1: unknown): ReactNode => {
    if (isViewObjectArg(arg1)) {
        const value = arg1.value
        const text = typeof value === 'string' ? value : formatMinutes(value as number | null | undefined)
        return wrapViewValue(arg1.field, text)
    }
    return formatMinutes(arg1 as number | null | undefined)
}

/** Мінімум полів батьківської задачі, потрібний посиланню. */
interface ParentTaskRef {
    uuid?: string
    number?: number
    title?: string
}

const isParentTaskRef = (value: unknown): value is ParentTaskRef =>
    value !== null && typeof value === 'object' && 'uuid' in (value as object)

/**
 * `parent` на view-картці — ПОСИЛАННЯ на батьківське завдання: клік відкриває
 * його картку через `OpenResourceCardTask` (resource 'Task' — той самий
 * механізм, що вузол дерева і тап по дзвіночку). Без батька — риска, як і
 * досі (поле не ховаємо: його наявність показує, що задача коренева).
 * Підтримує обидві конвенції виклику ядра (див. шапку файла).
 */
export const renderParentLink = (arg1: unknown): ReactNode => {
    if (isViewObjectArg(arg1)) {
        const value = arg1.value
        // Об'єктний ре-виклик: value — вже готовий результат позиційного
        // (посилання-елемент або «—») — лише загортаємо в шаблон із лейблом
        if (!arg1.field) return value as ReactNode
        return <ViewFieldTemplate field={arg1.field}>{value as ReactNode}</ViewFieldTemplate>
    }

    if (!isParentTaskRef(arg1) || !arg1.uuid) return '—'

    const { uuid, number, title } = arg1
    return (
        <Typography.Link
            onClick={() => {
                void OpenResourceCardTask({ resource: 'Task', primaryFieldValue: uuid, mode: 'view' })
            }}
        >
            №{number} {title}
        </Typography.Link>
    )
}
